<template lang="pug">
v-card.preview.summernote-content
  div.d-flex.justify-space-between.pl-6.pr-3.align-center.card-header
    h3 {{ title || 'Question Preview' }}
    div.left
      v-menu.question-menu__wrapper
        template(v-slot:activator="{ on }")
          v-icon(v-on="on").copy-arrow mdi-dots-horizontal
        v-list.question-menu
          v-list-item(@click="onEditClick").question-menu__item
            v-list-item-title Edit
          v-list-item(@click="showDeleteConfirmation").question-menu__item
            v-list-item-title Delete
      v-btn(icon @click="hideQuestionPreview()")
        v-icon mdi-close
  v-divider
  div.body.no-footer(v-if="model")
    v-form.px-6.pt-4(ref="form" v-if="model")
      v-row
        v-col.license-types.py-0(cols="12")
          div.license-types__label License type
          div.license-types__row
            v-checkbox.mr-4(
              v-model="model.is_auto"
              label="Auto"
              disabled
            )
            v-checkbox.mr-4(
              v-model="model.is_motor"
              label="Moto"
              disabled
            )
            v-checkbox(
              v-model="model.is_scooter"
              label="Scooter"
              disabled
            )
      v-row
        v-col(cols="6")
          div.category(v-if="category")
            span.category__label Category
            p.category__name {{ category.name }}
        v-col(cols="6")
          v-autocomplete(
            label='Layout'
            placeholder="Choose Layout"
            v-model="model.layout"
            :items="LAYOUT_OPTIONS"
            item-value="query"
            item-text="name"
            dense
            disabled
          )
        v-col(cols='12').py-0
          v-text-field(
            label='Short name'
            v-model="model.name"
            dense
            disabled
          )
        v-col(
          v-show="showQuestion"
          cols='12'
          order="first"
        ).py-0
          div.ql-editor(v-html="model.content").no-error
        v-col(
          v-if="showVideo && video && video.universalHd"
          cols='12'
          order="first"
        ).py-0.pb-4
          div
            nu-player(
              v-if="!isVideoProcessing && video.status === 'ACTIVE'"
              :video-file="video"
              :video-files="model.videos"
              :poster="model.thumbnail"
            )
        v-col(
          v-if="video && video.encoding_job && video.status !== 'ACTIVE'"
          cols='12'
          order="first"
        ).py-0.pb-4
            video-encoding-status(
              :encoding-job="video.encoding_job"
              @status-updated="onVideoStatusUpdated"
            )
        v-col(
          cols="12"
          order="first"
          v-show="showThumbnail"
        )
          .image-wrapper
            video(
              v-if="model.isVideoThumbnail"
              :src="model.thumbnail"
              controls="controls"
            ).thumbnail
            img(
              v-else-if="model.thumbnail"
              :src="model.thumbnail"
            ).thumbnail
            .pointer-positions(v-if="model.layout === LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP")
              .pointer-position(
                v-for="(pointer, index) in model.pointers"
                :key="`pointer-position-${index}`"
                :style="{'left': `${pointer.coordinates.x}%`, 'top': `${pointer.coordinates.y}%`}"
              )
          .pointer-values
            .pointer-value(
              v-for="(pointer, index) in model.pointers"
              :key="`pointer-value-${index}`"
            ) {{ pointer.value }}
        v-col.multiple-answers-toggle.pb-0(
          cols="12"
          v-if="[LAYOUT_OPTIONS_QUERY.PICTURE_SELECT, LAYOUT_OPTIONS_QUERY.QUESTIONS].includes(model.layout)"
        )
          v-checkbox.mr-4(
            v-model="model.show_multiple_answers_hint"
            label="Show multiple answers hint"
            disabled
          )
        v-col(
          cols="12"
          order="first"
          v-show="showAnswers"
        ).py-0
          .d-flex.align-center(v-for="(answer, index) in model.choices")
            v-checkbox(
              v-model="answer.isRight"
              disabled
            ).no-error.mt-0
            v-text-field(
              label='Answer title'
              v-model="answer.title"
              disabled
              single-line
              dense
            ).no-error
        v-col(
          cols="6"
          v-if="isNumberLayout"
        )
          h4 Answer
          v-text-field(
            label='Answer'
            v-model="model.numeric_answer"
            disabled
            type="number"
            single-line
          )
        v-col(
          cols="6"
          v-if="isNumberLayout"
        )
          h4 Measurement units
          v-text-field(
            label='Measurement units'
            v-model="model.measurement_units"
            disabled
            single-line
          )
        v-col(
          cols="12"
          v-if="isPictureLayout"
        )
          h4 Answers
          div.picture-answers
            div.picture-answers__item(
              v-for="(answer, index) in model.choices"
              :key="index"
            )
              v-checkbox(
                v-model="answer.isRight"
                disabled
              ).no-error.mt-0.mr-2
              a.picture-answers__item-image(
                :href="answer.picture"
                target="_blank"
              )
                img(:src="answer.picture")
        v-col(
          cols="12"
          v-show="showExplanation"
        ).py-0
          div(v-html="model.comment")
        v-col.pb-8
  confirmation-dialog(
    v-model="deleteConfirmationModal"
    title="Are you sure to delete this question?"
    @okBtnClicked="deleteQuestion"
  )
</template>
<script>
import ErrorMixin from "@/mixins/errors.mixin"
import QuestionItemModel from "./core/models/questionModel"
import QuestionSvc from "./core/question-service"
import TrainingSvc from "@/app/exercise/modules/trainings/core/training-service"
import FormErrors from "@/util/form-errors"
import {
  LAYOUT_OPTIONS,
  LAYOUT_OPTIONS_QUERY,
  PREFILL_GEVAARHERKENNING_VALUES,
  PREFILL_GEVAARHERKENNING_EMPTY_STATEMENTS
} from "./core/question-const"
import videoStatusPollerMixin from "@/mixins/video-status-poller.mixin";

export default {
  components: {
    confirmationDialog: () => import('@/components/global/ConfirmationDialog.vue'),
    NuPlayer: () => process.browser ? import('vue-nu-video-player').then(module => module.NuPlayer) : null,
    VideoEncodingStatus: () => import('./VideoEncodingStatus.vue'),
  },
  mixins: [ErrorMixin, videoStatusPollerMixin],
  props: {
    title: String,
    itemModel: {
      default: null,
      type: Object,
    },
    layout: [String, Array],
  },
  computed: {
    isVideoProcessing() {
      return this.model.video && this.model.video.encoding_job;
    },
    category() {
      return this.$store.state.trainingCategories.items ? this.$store.state.trainingCategories.items.find(item => item.id === this.model.categoryId) : {}
    },
    showAnswers() {
      return this.model.choices.length && ![LAYOUT_OPTIONS_QUERY.THEORIE, LAYOUT_OPTIONS_QUERY.VIDEO, LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP, LAYOUT_OPTIONS_QUERY.NUMERIC, LAYOUT_OPTIONS_QUERY.PICTURE_SELECT].includes(this.model.layout)
    },
    showExplanation() {
      return !!this.model.comment
    },
    showThumbnail() {
      return !!this.model.thumbnail
    },
    showQuestion() {
      return !!this.model.content
    },
    showVideo() {
      return !!this.model.video
    },
    isNumberLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.NUMERIC;
    },
    isPictureLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.PICTURE_SELECT;
    },
    video() {
      return {
        iFullHd: this.model.video?.ios_full_hd,
        iosHd: this.model.video?.ios_hd,
        iosSd: this.model.video?.ios_sd,
        universalFullHd: this.model.video?.universal_full_hd,
        universalHd: this.model.video?.universal_hd,
        universalSd: this.model.video?.universal_sd,
        status: this.model.video?.status,
        encoding_job: this.model.video?.encoding_job,
      }
    }
  },
  data: () => ({
    svc: null,
    trainingSvc: null,
    formErrors: null,
    model: new QuestionItemModel(),
    LAYOUT_OPTIONS,
    LAYOUT_OPTIONS_QUERY,
    PREFILL_GEVAARHERKENNING_VALUES,
    PREFILL_GEVAARHERKENNING_EMPTY_STATEMENTS,
    deleteConfirmationModal: false,
  }),
  watch: {
    async itemModel() {
      this.setModelFromProp();
      await this.$nextTick();
      this.resetPolling();
    },
  },
  inject: [
    'toggleQuestionPreview',
    'showItemEdit'
  ],
  methods: {
    onEditClick() {
      this.showItemEdit(this.model);
    },
    showDeleteConfirmation() {
      this.deleteConfirmationModal = true;
    },
    async deleteQuestion() {
      await this.trainingSvc.remove(this.model);
      this.hideQuestionPreview();
      this.$emit('deleteQuestion', this.model);
    },
    init() {
      this.svc = new QuestionSvc()
      this.trainingSvc = new TrainingSvc()
      this.formErrors = new FormErrors()
      this.setModelFromProp();
    },
    hideQuestionPreview() {
      this.toggleQuestionPreview();
    },
    setModelFromProp() {
      Object.assign(this.model, this.itemModel);
    },
    onVideoStatusUpdated(video) {
      if (video) {
        this.model.video = video;
      }
    },
  },
}
</script>
<style lang="scss" scoped>
  @import "@/assets/scss/summernote-content.scss";
  .preview .body.no-footer{
    overflow: auto;
  }
  .preview {
    .v-input::v-deep {
      .v-label {
        color: $label-color;
      }
      input {
        color: $input-color;
      }
      .v-input__append-inner {
        opacity: 0;
      }
      .v-input__slot {
        &:before {
          display: none;
        }
      }
    }
  }
  .thumbnail {
    max-width: 100%;
    max-height: 200px;
  }
  .ql-editor::v-deep {
    img {
      max-width: 100%;
    }
    .table-bordered {
      border-collapse: collapse;
      border-spacing: 0;
      border: 2px solid #ddd;
      width: 100%;
      max-width: 100%;
      margin-bottom: 20px;
      & > tbody > tr > td {
        border: 1px solid #ddd;
        padding: 8px;
      }
    }
  }
  .category{
    margin-top: -18px;
    &__label{
      color: $label-color;
      font-size: 12px;
    }
  }

  .image-wrapper{
    display: inline-block;
    position: relative;

    .pointer-position{
      width: 30px;
      height: 30px;
      position: absolute;
      border-radius: 50%;
      border: 2px dashed white;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: 1;
      transform: translateX(-50%) translateY(-50%);
    }
  }
  .pointer-values{
    margin-top: 8px;
    display: flex;

    .pointer-value{
      width: 30px;
      height: 30px;
      background-color: $primar-color-light;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      border: 2px solid white;
      position: relative;
      margin-right: 16px;

      &:before{
        content: "";
        position: absolute;
        top: -3px;
        left: -3px;
        right: -3px;
        bottom: -3px;
        border: 2px solid $primar-color-light;
        border-radius: 50%;
      }
    }
  }
  .question-menu{
    &__item{
      cursor: pointer;
    }
    
    &__wrapper {
      position: relative;
      z-index: 10;
    }
  }

  .license-types {
    margin-bottom: 8px;

    &__label {
      color: $label-color;
      font-weight: 400;
      font-size: 12px;
      margin-bottom: 2px;
    }

    &__row {
      display: flex;
      align-items: center;

      &::v-deep {
        label {
          font-weight: 400;
          margin-bottom: 0;
        }

        .v-input {
          margin-top: 0;
        }

        .v-messages {
          display: none;
        }
      }
    }
  }

  .picture-answers {
    margin-top: 8px;

    &__item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      &-image {
        display: block;
        height: 60px;
        aspect-ratio: 16 / 9;
        cursor: pointer;
        border-radius: 4px;
        overflow: hidden;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
      }
    }
  }






</style>
